'use client'

import clsx from 'clsx'

import useVisibleSection from '@/shared/hooks/useVisibleSection'

export type TableOfContentItem = {
  title: string
  elementId: string
}

type TableOfContentProps = {
  items: Array<TableOfContentItem>
}

export default function TableOfContent({ items }: TableOfContentProps) {
  const visibleSection = useVisibleSection(
    items.map(({ elementId }) => elementId),
  )

  if (items.length === 0) return null

  return (
    <>
      <details
        className="group rounded-2xl border border-[#ECECEF] px-5 py-[18px] lg:hidden"
        open
      >
        <summary className="flex cursor-pointer list-none items-center justify-between text-[11.5px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-[#82828B] [&::-webkit-details-marker]:hidden">
          On this page
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="transition-transform duration-500 group-open:rotate-180"
          >
            <path
              d="m6 9 6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>
        <TableOfContentLinks
          items={items}
          visibleSection={visibleSection}
          className="mt-3.5 gap-[11px]"
        />
      </details>

      <aside className="sticky top-[118px] hidden max-h-[calc(100dvh-142px)] w-[260px] flex-col gap-3.5 self-start overflow-hidden lg:flex">
        <p className="text-[11.5px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-[#82828B]">
          On this page
        </p>
        <TableOfContentLinks
          items={items}
          visibleSection={visibleSection}
          className="min-h-0 flex-1 gap-3 overflow-y-auto overscroll-contain pb-2 pr-2 [scrollbar-gutter:stable] [scrollbar-width:thin]"
        />
      </aside>
    </>
  )
}

function TableOfContentLinks({
  items,
  visibleSection,
  className,
}: {
  items: Array<TableOfContentItem>
  visibleSection: string | undefined
  className?: string
}) {
  return (
    <ul className={clsx('flex flex-col', className)}>
      {items.map(({ title, elementId }, index) => {
        const selected =
          visibleSection === elementId || (!visibleSection && index === 0)

        return (
          <li key={elementId}>
            <button
              type="button"
              className={clsx(
                'flex w-full items-start gap-2 text-left text-sm leading-[19px] transition-colors duration-500 hover:text-[#0B0B0F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]',
                selected
                  ? 'font-semibold text-[#0B0B0F]'
                  : 'pl-3.5 text-[#82828B]',
              )}
              onClick={() => {
                const element = document.getElementById(elementId)
                if (!element) return

                window.scrollTo({
                  top:
                    element.getBoundingClientRect().top + window.scrollY - 110,
                  behavior: 'smooth',
                })
              }}
            >
              {selected ? (
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C5CD6]" />
              ) : null}
              {title}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
